import { useEffect, useCallback, useMemo, useRef } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { getIncomingCalls } from '../../../store/reducers/IncomingCallsSlice';
import { getClinics } from '../../../store/reducers/ClinicSlice';
import { getSurnameDoctor } from '../../../store/reducers/surnameDoctor';
import {
  getIncomingReport,
  selectIncomingReportLoading,
} from '../../../store/reducers/IncomingReportSlice';
import { useDebounce } from '../../shared/useDebounce';
import { useIncomingCallsColumns } from './useIncomingCallsColumns/useIncomingCallsColumns';
import { useIncomingCallsHandlers } from './useIncomingCallsHandlers/useIncomingCallsHandlers';
import { useIncomingCallsState } from './useIncomingCallsState/useIncomingCallsState';
import { useFilterOption } from '../../shared/useFilterOption';
import { useJournalCampaignFilter } from '../../shared/useJournalCampaignFilter';
import { createServerSortChangeHandler } from '../../shared/useServerTableSort';
import {
  incomingJournalSortMap,
  incomingServerSortDefaults,
} from '../../shared/journalSortMaps';
import { IncomingCallsTablet } from '../../../models/IncomingCalls/IncomingCalls';
import { IncomingCallsRequest } from '../../../store/reducers/IncomingCallsSlice';
import {
  JournalFilters,
  useJournalQueryState,
} from '../../shared/useJournalQueryState';

const toDate = (value: string) => new Date(`${value}T00:00:00`);
const toSortDirection = (value: string): JournalFilters['sortDirection'] =>
  value === 'Desc' ? 'desc' : 'asc';

export const useIncomingCalls = () => {
  const isReportLoading = useAppSelector(selectIncomingReportLoading);

  const {
    clinics,
    specializations,
    surnameDoctors,
    totalCount,
    status,
    setIndex,
    pageSize,
    setPageSize,
    callRequest,
    setCallRequest,
    callRequestReport,
    setCallRequestReport,
    total,
    currentIncomingCalls,
    allCalls,
  } = useIncomingCallsState();

  const dispatch = useAppDispatch();

  const journalFilters = useMemo<JournalFilters>(
    () => ({
      dateRange: [
        toDate(callRequest['Filter.From']),
        toDate(callRequest['Filter.To']),
      ],
      textSearch: callRequest['Filter.TextSearch'],
      status: callRequest['Filter.States'],
      scenario: callRequest['Filter.Types'] ?? [],
      campaign: [
        ...(callRequest['Filter.FormBatchIds'] ?? []),
        ...(callRequest['Filter.DispensarizationBatchIds'] ?? []),
      ],
      medicalOrganization: callRequest['Filter.ClinicIds'],
      result:
        callRequest['Filter.Result'] ?? callRequest['Filter.Results'] ?? [],
      attemptStatus: callRequest['Filter.AttemptStates'],
      pageIndex: callRequest.PageIndex,
      pageSize: callRequest.PageSize,
      sortField: callRequest.SortKeySelector,
      sortDirection: toSortDirection(callRequest.SortOrder),
    }),
    [callRequest],
  );

  const {
    activeFilterCount,
    isLoading: dictionariesLoading,
    dictionaries,
    scenarioLabelByType,
    resultLabelByValue,
  } = useJournalQueryState(journalFilters, 'inbound');

  useEffect(() => {
    dispatch(getClinics());
    dispatch(getSurnameDoctor());
  }, []);

  const debouncedTextSearch = useDebounce(
    callRequest['Filter.TextSearch'],
    500,
  );

  const debouncedClinic = useDebounce(callRequest['Filter.ClinicIds'], 500);
  const debouncedStates = useDebounce(callRequest['Filter.States'], 500);
  const debouncedAttemptStates = useDebounce(
    callRequest['Filter.AttemptStates'],
    500,
  );
  const debouncedResults = useDebounce(callRequest['Filter.Results'], 500);
  const debouncedTypes = useDebounce(callRequest['Filter.Types'], 500);

  const dateRange = useMemo<[Dayjs, Dayjs]>(
    () => [dayjs(callRequest['Filter.From']), dayjs(callRequest['Filter.To'])],
    [callRequest['Filter.From'], callRequest['Filter.To']],
  );

  const prevTextSearchRef = useRef<string>('');

  const onTableChange = createServerSortChangeHandler<
    IncomingCallsTablet,
    IncomingCallsRequest
  >(
    callRequest,
    incomingJournalSortMap,
    setCallRequest,
    incomingServerSortDefaults,
  );

  useEffect(() => {
    const isTextSearch =
      prevTextSearchRef.current?.length !== debouncedTextSearch.length;
    const isTextSearchDelete =
      prevTextSearchRef.current?.length > debouncedTextSearch.length;

    prevTextSearchRef.current = debouncedTextSearch;
    if (isTextSearch) {
      if (debouncedTextSearch || isTextSearchDelete) {
        if (isTextSearchDelete) {
          dispatch(getIncomingCalls(callRequest));
        } else if (
          debouncedTextSearch.length > 2 ||
          debouncedTextSearch.length == 0
        ) {
          dispatch(getIncomingCalls(callRequest));
        }
      }
    } else if (
      debouncedResults ||
      debouncedStates ||
      debouncedAttemptStates ||
      debouncedTypes
    ) {
      dispatch(getIncomingCalls(callRequest));
    } else {
      dispatch(getIncomingCalls(callRequest));
    }
  }, [
    callRequest['Filter.From'],
    callRequest['Filter.To'],
    callRequest['Filter.FormTemplateIds'],
    callRequest['Filter.FormBatchIds'],
    callRequest['Filter.DispensarizationBatchIds'],
    callRequest.PageIndex,
    callRequest.PageSize,
    callRequest.SortKeySelector,
    callRequest.SortOrder,
    debouncedTextSearch,
    debouncedClinic,
    debouncedStates,
    debouncedAttemptStates,
    debouncedResults,
    debouncedTypes,
  ]);

  useEffect(() => {
    if (Math.floor(total / callRequest.PageSize) < callRequest.PageIndex) {
      setCallRequest((prev) => ({
        ...prev,
        PageIndex: Math.floor(total / callRequest.PageSize),
      }));
    }
  }, [total]);

  const { COLUMNS } = useIncomingCallsColumns({
    callRequest,
    scenarioLabelByType,
    resultLabelByValue,
  });

  const divider = {
    height: 1,
    backgroundColor: '#daccccff',
    margin: '0',
  };

  const {
    handleSearch,
    handleRangePicker,
    handleChange,
    handlePageSizeChange,
    clinicValue,
    handleClinicsChange,
    handleOpenClinics,
    taskStatusValue,
    handleTaskStatusChange,
    handleOpenTaskStatus,
    attemptStatusValue,
    handleAttemptStatusChange,
    handleOpenAttemptStatus,
    resultValue,
    handleResultChange,
    handleOpenResult,
    scenarioValue,
    handleScenarioChange,
    handleOpenScenario,
    resetFilters,
    isResetDisabled,
  } = useIncomingCallsHandlers({
    onIndex: setIndex,
    onCallRequest: setCallRequest,
    callRequest,
    onCallRequestReport: setCallRequestReport,
    callRequestReport,
    onPageSize: setPageSize,
    total,
  });

  const handleOkReport = useCallback(() => {
    dispatch(getIncomingReport(callRequestReport));
  }, [dispatch, callRequestReport]);

  const filterOption = useFilterOption();
  const campaignFilter = useJournalCampaignFilter({
    types: callRequest['Filter.Types'] ?? [],
    callRequest,
    onCallRequest: setCallRequest,
    callRequestReport,
    onCallRequestReport: setCallRequestReport,
  });
  const isTableLoading = status.responseLoading;
  const isFilterLoading = isTableLoading || dictionariesLoading;

  return {
    campaignFilter,
    setCallRequest,
    callRequest,
    dateRange,
    clinics,
    pageSize,
    isTableLoading,
    hasLoadError: status.responseError !== null,
    isFilterLoading,
    totalCount,
    total,
    handleChange,
    currentIncomingCalls,
    specializations,
    surnameDoctors,
    handleRangePicker,
    handleSearch,
    allCalls,
    COLUMNS,
    divider,
    handlePageSizeChange,
    onTableChange,
    handleOkReport,
    filterOption,
    clinicValue,
    handleClinicsChange,
    handleOpenClinics,
    taskStatusValue,
    handleTaskStatusChange,
    handleOpenTaskStatus,
    attemptStatusValue,
    handleAttemptStatusChange,
    handleOpenAttemptStatus,
    resultValue,
    handleResultChange,
    handleOpenResult,
    scenarioValue,
    handleScenarioChange,
    handleOpenScenario,
    resetFilters,
    isResetDisabled,
    activeFilterCount,
    dictionariesLoading,
    taskStatusOptions: dictionaries.statuses,
    resultOptions: dictionaries.results,
    scenarioOptions: dictionaries.scenarios,
    scenarioLabelByType,
    resultLabelByValue,
    isReportLoading,
  };
};
